<script setup lang="ts" generic="T">
	import { computed, ref } from 'vue'
	import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
	import type { ClassNameValue } from 'tailwind-merge'

	interface Props {
		items: T[]
		itemHeight: number | Record<string, number>
		cols?: number | Record<string, number>
		gap?: number
		as?: string
		itemAs?: string
		containerClass?: ClassNameValue
		listClass?: ClassNameValue
		itemClass?: ClassNameValue
		height?: string | number
		threshold?: number
	}

	const props = withDefaults(defineProps<Props>(), {
		cols: 1,
		gap: 0,
		as: 'ul',
		height: '400px'
	})

	const emit = defineEmits<{
		(e: 'scroll', value: number): void
		(e: 'onSelect', value: T): void
	}>()

	const effectiveItemAs = computed(() => {
		if (props.itemAs) return props.itemAs
		return props.as === 'ul' ? 'li' : 'div'
	})

	const breakpoints = useBreakpoints(breakpointsTailwind)

	const getActiveValue = (val: number | Record<string, number>) => {
		if (typeof val === 'number') return val
		if (typeof val === 'object') {
			let matched = val.default || 0
			if (breakpoints.sm.value && val.sm) matched = val.sm
			if (breakpoints.md.value && val.md) matched = val.md
			if (breakpoints.lg.value && val.lg) matched = val.lg
			if (breakpoints.xl.value && val.xl) matched = val.xl
			if (breakpoints['2xl'].value && val['2xl']) matched = val['2xl']
			return matched
		}
		return 0
	}

	const activeCols = computed(() => getActiveValue(props.cols) || 1)
	const activeItemHeight = computed(() => getActiveValue(props.itemHeight) || 100)

	const isVirtual = computed(() => props.items.length > (props.threshold ?? 100))

	const scrollTop = ref(0)

	const onScroll = (e: Event) => {
		scrollTop.value = (e.target as HTMLElement).scrollTop
		emit('scroll', scrollTop.value)
	}

	const containerHeight = computed(() => {
		if (typeof props.height === 'number') return `${props.height}px`
		return props.height
	})

	const rowCount = computed(() => Math.ceil(props.items.length / activeCols.value))
	const totalHeight = computed(() => {
		const rows = rowCount.value
		if (rows === 0) return 0
		return rows * activeItemHeight.value + (rows - 1) * props.gap
	})

	const visibleItems = computed(() => {
		const { gap, items } = props
		const cols = activeCols.value
		const itemHeight = activeItemHeight.value
		const viewHeight = parseInt(containerHeight.value)

		const startRow = Math.floor(scrollTop.value / (itemHeight + gap))
		const endRow = Math.ceil((scrollTop.value + viewHeight) / (itemHeight + gap))

		const buffer = 2 // rows
		const actualStartRow = Math.max(0, startRow - buffer)
		const actualEndRow = Math.min(rowCount.value, endRow + buffer)

		const startIdx = actualStartRow * cols
		const endIdx = actualEndRow * cols

		return items.slice(startIdx, endIdx).map((data, i) => {
			const globalIdx = startIdx + i
			const row = Math.floor(globalIdx / cols)
			const col = globalIdx % cols

			return {
				data,
				index: globalIdx,
				style: {
					position: 'absolute',
					top: `${row * (itemHeight + gap)}px`,
					left: `calc(${col} * (100% - ${(cols - 1) * gap}px) / ${cols} + ${col * gap}px)`,
					width: `calc((100% - ${(cols - 1) * gap}px) / ${cols})`,
					height: `${itemHeight}px`
				}
			}
		})
	})
</script>

<template>
	<div
		v-if="isVirtual"
		ref="containerRef"
		:class="cn('relative overflow-y-auto', containerClass)"
		:style="{ height: containerHeight }"
		@scroll="onScroll"
	>
		<div :style="{ height: `${totalHeight}px`, width: '100%' }" class="relative">
			<component :is="as" :class="cn('absolute left-0 top-0 m-0 w-full list-none p-0', listClass)">
				<template v-for="item in visibleItems" :key="item.index">
					<slot name="item" :item="item.data" :index="item.index" :style="item.style">
						<component
							:is="effectiveItemAs"
							:class="cn(itemClass)"
							:style="item.style"
							@click="emit('onSelect', item.data)"
						>
							<slot :item="item.data" :index="item.index" />
						</component>
					</slot>
				</template>
			</component>
		</div>
	</div>
	<div v-else :class="cn(containerClass)">
		<component :is="as" :class="cn('m-0 list-none space-y-3 p-0', listClass)">
			<template v-for="(item, index) in items" :key="index">
				<slot name="item" :item="item" :index="index" :style="{}">
					<component :is="effectiveItemAs" :class="cn(itemClass)">
						<slot :item="item" :index="index" />
					</component>
				</slot>
			</template>
		</component>
	</div>
</template>
